import { useState, useRef } from "react";
import { ToolsShell, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Before/After Overlay — two image uploads, swipe divider. Pure local;
// no backend.

export default function BeforeAfter() {
  const [imgA, setImgA] = useState(null);
  const [imgB, setImgB] = useState(null);
  const [pos, setPos] = useState(50); // 0..100, divider position
  const [orientation, setOrientation] = useState("vertical"); // vertical | horizontal
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const onFile = (setter) => (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setter(URL.createObjectURL(f));
  };

  const onPointerDown = (e) => {
    draggingRef.current = true;
    onPointerMove(e);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const c = containerRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    if (orientation === "vertical") {
      const x = (e.clientX || (e.touches && e.touches[0].clientX) || 0) - rect.left;
      setPos(Math.max(0, Math.min(100, (x / rect.width) * 100)));
    } else {
      const y = (e.clientY || (e.touches && e.touches[0].clientY) || 0) - rect.top;
      setPos(Math.max(0, Math.min(100, (y / rect.height) * 100)));
    }
  };
  const onPointerUp = () => { draggingRef.current = false; };

  return (
    <ToolsShell title="Before / After">
      {(!imgA || !imgB) ? (
        <>
          <p style={{ fontSize: 14, color: "#C8CADF", marginTop: 0 }}>
            Upload two photos to compare side-by-side with a swipeable divider. Renovation progress, plant growth, cleaning, art — anything.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
            <UploadCard label="Before" img={imgA} onChange={onFile(setImgA)} />
            <UploadCard label="After" img={imgB} onChange={onFile(setImgB)} />
          </div>
          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
            Files never leave your device. Best with photos taken from the same position.
          </p>
        </>
      ) : (
        <>
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            {[{ id: "vertical", label: "Vertical divider" }, { id: "horizontal", label: "Horizontal divider" }].map((o) => (
              <button
                key={o.id}
                onClick={() => setOrientation(o.id)}
                style={{ background: orientation === o.id ? TOOLS_ACCENT : "#1C1E2E", color: orientation === o.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", flex: 1 }}
              >
                {o.label}
              </button>
            ))}
          </div>

          <div
            ref={containerRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            style={{
              position: "relative",
              width: "100%",
              aspectRatio: "4 / 3",
              borderRadius: 12,
              overflow: "hidden",
              userSelect: "none",
              cursor: orientation === "vertical" ? "ew-resize" : "ns-resize",
              touchAction: "none",
              background: "#000",
            }}
          >
            <img src={imgA} alt="Before" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{
              position: "absolute",
              ...(orientation === "vertical"
                ? { top: 0, bottom: 0, left: 0, width: `${pos}%` }
                : { left: 0, right: 0, top: 0, height: `${pos}%` }),
              overflow: "hidden",
            }}>
              <img src={imgB} alt="After" style={{
                position: "absolute",
                ...(orientation === "vertical"
                  ? { top: 0, left: 0, height: "100%", width: `${10000 / pos}%`, maxWidth: "none" }
                  : { left: 0, top: 0, width: "100%", height: `${10000 / pos}%`, maxHeight: "none" }),
                objectFit: "cover",
              }} />
            </div>
            {/* Divider line + handle */}
            <div style={{
              position: "absolute",
              ...(orientation === "vertical"
                ? { top: 0, bottom: 0, left: `${pos}%`, width: 2, transform: "translateX(-1px)" }
                : { left: 0, right: 0, top: `${pos}%`, height: 2, transform: "translateY(-1px)" }),
              background: "#fff",
              boxShadow: "0 0 4px rgba(0,0,0,0.5)",
            }} />
            <div style={{
              position: "absolute",
              ...(orientation === "vertical"
                ? { top: "50%", left: `${pos}%`, transform: "translate(-50%, -50%)" }
                : { left: "50%", top: `${pos}%`, transform: "translate(-50%, -50%)" }),
              width: 36, height: 36, borderRadius: "50%",
              background: "#fff",
              boxShadow: "0 0 6px rgba(0,0,0,0.4)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: TOOLS_ACCENT, fontWeight: 800, fontSize: 18,
            }}>
              {orientation === "vertical" ? "⇆" : "⇅"}
            </div>
            <Label position="top-left" text="BEFORE" />
            <Label position={orientation === "vertical" ? "top-right" : "bottom-left"} text="AFTER" />
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            <label style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
              <input type="file" accept="image/*" onChange={onFile(setImgA)} style={{ display: "none" }} />
              Change Before
            </label>
            <label style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
              <input type="file" accept="image/*" onChange={onFile(setImgB)} style={{ display: "none" }} />
              Change After
            </label>
            <button onClick={() => setPos(50)} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
              Reset divider
            </button>
          </div>
        </>
      )}
    </ToolsShell>
  );
}

function UploadCard({ label, img, onChange }) {
  return (
    <label style={{ display: "block", cursor: "pointer", background: "#13141F", border: "1px dashed #262839", borderRadius: 12, padding: "20px 16px", textAlign: "center", aspectRatio: "1 / 1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
      <input type="file" accept="image/*" onChange={onChange} style={{ display: "none" }} />
      {img ? (
        <img src={img} alt={label} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <>
          <div style={{ fontSize: 24, color: TOOLS_ACCENT, marginBottom: 8 }}>+</div>
          <div style={{ fontSize: 13, color: "#9A9DBE", fontWeight: 600 }}>{label}</div>
          <div style={{ fontSize: 11, color: "#5B5E7A", marginTop: 4 }}>Tap to upload</div>
        </>
      )}
      {img && (
        <div style={{ position: "absolute", top: 6, left: 8, background: "rgba(0,0,0,0.7)", color: "#fff", padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{label}</div>
      )}
    </label>
  );
}

function Label({ position, text }) {
  const styleByPos = {
    "top-left": { top: 10, left: 10 },
    "top-right": { top: 10, right: 10 },
    "bottom-left": { bottom: 10, left: 10 },
    "bottom-right": { bottom: 10, right: 10 },
  };
  return (
    <div style={{ position: "absolute", ...styleByPos[position], background: "rgba(0,0,0,0.6)", color: "#fff", padding: "4px 10px", borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
      {text}
    </div>
  );
}
